import { For, Show, createMemo, lazy, onCleanup, onMount } from "solid-js";

import Buoy from "@/components/Base/Buoy/Buoy.tsx";
import DialogSettingCenter from "@/components/Dialogs/DialogCenter";
import { getIndexComponentLists } from "@/hooks/useConfig";
import { usePageScrollDetection } from "@/hooks/usePageScrollDetection";
import { getHomePopups } from "@/hooks/usePopup";
import { usePreload } from "@/hooks/usePreload.ts";
import { isMobile } from "@/lib/isMobile";
import { sdkManager } from "@/lib/sdk/sdkManager";
import { getAdtype } from "@/lib/utils";
import { checkModalPath } from "@/router/modalRoutes";
import {
  FooterComponents,
  getRenderedStatus,
  updateRenderedMap,
} from "@/stores/configStore";
import { CompType } from "@/types/compontent/base/baseEnum";
import { CompontentFactory } from "@/types/compontent/compontentFactory";
import { ComponentInfo } from "@/types/swagger/front";
import { useLocation } from "@solidjs/router";

// 预加载关键组件
const preloadComponents = () => {
  const components = ["banner", "carousel"];
  components.forEach((type) => {
    import(`@/components/H5/Components/${type}/index.tsx`);
  });
};
function RenderIndexComponent({ item }: { item: ComponentInfo }) {
  // 使用 CompontentFactory 创建组件实例
  const factory = CompontentFactory.getInstance();
  const component = factory.createComponent({
    ...item,
    type: item.type as CompType,
  });

  onCleanup(() => {
    try {
      if (component && !component.isDestroyed()) {
        factory.destroyComponent(component);
      }
    } catch (error) {
      console.error("Error destroying component:", error);
    }
  });

  // 动态导入组件
  const DynamicComponent = lazy(
    () => import(`@/components/H5/Components/${item.type}/index.tsx`),
  );

  const placeHeight = createMemo(() => {
    return getRenderedStatus(item.key)
      ? "unset"
      : isMobile()
        ? item.height
        : item.pc_height;
  });
  if (!item.delay) {
    setTimeout(() => {
      updateRenderedMap(item.key, true);
    }, 100);
  }
  return (
    <div style={{ "min-height": placeHeight() }}>
      <DynamicComponent {...component} />
    </div>
  );
}

export default function H5HomePage() {
  const location = useLocation();
  usePreload({
    timmer: 200,
    preloads: [lazy(() => import("@/pages/h5/gamedetail"))],
  });

  onMount(() => {
    usePageScrollDetection("home_page_scroll", 0.5);
    // 清理之前的弹窗队列，确保弹窗状态正确重置
    DialogSettingCenter.clearQueue();
    sdkManager.setStartTime("home_page", Math.floor(Date.now() / 1000));
    sdkManager.tdReportEvent("home_page_visit", {
      ad_type: getAdtype(),
    });

    setTimeout(() => {
      // 过渡使用, modal route 会触发首页组件, 所以需要判断
      if (!checkModalPath(location.pathname)) {
        getHomePopups();
      }
    }, 500);
  });
  onCleanup(() => {
    sdkManager.setEndTime("home_page", Math.floor(Date.now() / 1000));
    sdkManager.tdReportEvent("home_page_stay_time", {
      page_view_stay_time: sdkManager.getTimeGap("home_page"),
    });
  });

  preloadComponents();

  const newList = createMemo(() => {
    return getIndexComponentLists()?.filter((item) => {
      return !FooterComponents.includes(item.type);
    });
  });

  const renderedList = createMemo(() => newList());

  const COMP_NO_NEED_WRAP = [
    CompType.banner,
    CompType.carousel,
    CompType.winner,
    // CompType.competition,
    CompType.activity_banner,
    // "game_list",
    // "guess_you_like",
    // "custom_list",
    // "maker_list",
    // "sport",
    // "bet_game",
  ];

  return (
    <div class="pt-(--module-spacing) relative w-full">
      <ul class="gap-(--module-spacing) flex flex-col">
        <For each={renderedList()} fallback={<></>}>
          {(item) => {
            return (
              <div
                class={COMP_NO_NEED_WRAP.includes(item.type) ? "" : "wrap-h5"}
              >
                <RenderIndexComponent item={item as ComponentInfo} />
              </div>
            );
          }}
        </For>
      </ul>
      <Buoy />
    </div>
  );
}
